import type { UseQueryResult } from "@tanstack/react-query";
import type { TFunction } from "i18next";
import type { UseFormReturn } from "react-hook-form";

import { useNavigate } from "@tanstack/react-router";
import { toast } from "sonner";

import type { AutoSuggestedWork, GetMyAccountQuery, GetMyProfileQuery, Setlist } from "@/api/queries";

import {
  usePostSetlist,
  usePutSetlist,
} from "@/api/mutations";
import { sendEvent, sendProperty } from "@/helpers/helpers";

import type { SetlistFormData } from "../utils/setlistCreateUpdateSchema";

import { buildSelectedSongs } from "../utils/setlistCreateUpdateUtils";

type UseSetlistMutationProps = {
  id: string | undefined;
  form: UseFormReturn<SetlistFormData>;
  cachedSongs: AutoSuggestedWork[];
  selectedAccount: string | null;
  myAccount: GetMyAccountQuery;
  profile: GetMyProfileQuery;
  setlist: UseQueryResult<Setlist[], Error>;
  setlists: UseQueryResult<Setlist[], Error>;
  t: TFunction;
  setPending: (v: boolean) => void;
  setMutationErrorMessage: (v: string) => void;
};

type UseSetlistMutationReturn = {
  addSetlist: (data: SetlistFormData) => Promise<void>;
  updateSetlist: (data: SetlistFormData) => Promise<void>;
};

export function useSetlistCreateUpdateMutation({
  id,
  form,
  cachedSongs,
  selectedAccount,
  myAccount,
  profile,
  setlist,
  setlists,
  t,
  setPending,
  setMutationErrorMessage,
}: UseSetlistMutationProps): UseSetlistMutationReturn {
  const navigate = useNavigate();
  const tuneCode = "";
  const createMutation = usePostSetlist();
  const updateMutation = usePutSetlist();

  const addSetlist = async (data: SetlistFormData): Promise<void> => {
    setPending(true);
    setMutationErrorMessage("");
    form.clearErrors("root");

    try {
      createMutation.reset();
      const selectedSongs = buildSelectedSongs(data.songList, cachedSongs);
      const coverSongs = (data.coverSongs ?? []).filter(
        item => (item.artist?.trim().length ?? 0) > 0
          || (item.song?.trim().length ?? 0) > 0
          || (item.tunecode?.trim().length ?? 0) > 0,
      );
      const payload = {
        account_code: selectedAccount,
        originator_code: myAccount.data?.originatorcode?.code,
        name: data.name,
        notes: data.notes,
        tuneCode,
        songs: JSON.stringify(selectedSongs),
        other_songs: JSON.stringify(coverSongs),
      };

      const response = await createMutation.mutateAsync(payload);

      if (response.failedvalidation) {
        form.setError("root", { message: String(response.data?.messages) });
        return;
      }

      toast.success(t("gigs:successfully-submitted-setlist-toast"));
      void navigate({ to: "/gigs/setlists" });
      void setlist.refetch();
      void setlists.refetch();
      sendProperty({
        property: "last_setlist_added",
        value: "CURRENT_DATE",
        email: profile?.data?.email,
      });
      sendEvent({ property: "pe14489857_setlist_added" });
    }
    catch (error) {
      setMutationErrorMessage(error instanceof Error ? error.message : String(error));
    }
    finally {
      setPending(false);
    }
  };

  const updateSetlist = async (data: SetlistFormData): Promise<void> => {
    setPending(true);
    setMutationErrorMessage("");
    form.clearErrors("root");

    try {
      updateMutation.reset();
      const selectedSongs = buildSelectedSongs(data.songList, cachedSongs);
      const coverSongs = (data.coverSongs ?? []).filter(
        item => (item.artist?.trim().length ?? 0) > 0
          || (item.song?.trim().length ?? 0) > 0
          || (item.tunecode?.trim().length ?? 0) > 0,
      );
      const payload = {
        id,
        account_code: selectedAccount,
        originator_code: myAccount.data?.originatorcode?.code,
        name: data.name,
        notes: data.notes,
        tuneCode,
        songs: JSON.stringify(selectedSongs),
        other_songs: JSON.stringify(coverSongs),
      };

      const response = await updateMutation.mutateAsync(payload);

      if (response.failedvalidation) {
        form.setError("root", { message: String(response.data?.messages) });
        return;
      }

      toast.success(t("gigs:successfully-updated-setlist-toast"));
      void navigate({ to: "/gigs/setlists" });
      void setlist.refetch();
      void setlists.refetch();
    }
    catch (error) {
      setMutationErrorMessage(error instanceof Error ? error.message : String(error));
    }
    finally {
      setPending(false);
    }
  };

  return { addSetlist, updateSetlist };
}
